import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useI18n } from "@wato/i18n/react";
import { Alert, Button, Checkbox, Input, Stack } from "@wato/ui";
import { useApi, useErrorMessage } from "../../api/context";
import { useAuth } from "../../auth/AuthProvider";
import { SETUP_ROUTE } from "../../auth/guards";
import { AuthLayout, safeNextPath } from "../../components/common";
import { loginErrorNeedsCredentialsHint } from "./loginErrors";
import { SocialAuthButtons } from "./SocialAuthButtons";

/** API codes the Google callback puts in `/login?error=` (ADR-0012), shown generically. */
const KNOWN_REDIRECT_ERRORS = new Set([
  "OAUTH_STATE_INVALID",
  "OAUTH_ACCESS_DENIED",
  "OAUTH_EXCHANGE_FAILED",
  "OAUTH_ID_TOKEN_INVALID",
  "AUTH_OAUTH_EMAIL_UNVERIFIED",
  "AUTH_OAUTH_CONFLICT",
  "AUTH_ACCOUNT_DISABLED",
]);

export function LoginPage() {
  const { t } = useI18n();
  const api = useApi();
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const errorMessage = useErrorMessage();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showHint, setShowHint] = useState(true);
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string }>({});

  const next = safeNextPath(params.get("next")) ?? "/";
  const redirectError = params.get("error");
  // The verification link lands on `/onboarding?emailVerified=1`, which sends a signed-out
  // user here with that path as `next`.
  const emailVerified = params.get("emailVerified") === "1" || next.includes("emailVerified=1");

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    const errors = {
      ...(email.trim() ? {} : { email: t("dashboard.auth.login.emailRequired") }),
      ...(password ? {} : { password: t("dashboard.auth.login.passwordRequired") }),
    };
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;
    setBusy(true);
    try {
      const result = await api.login({ email: email.trim(), password, rememberMe });
      if (result.status === "2fa_required") {
        await navigate(`/login/2fa?next=${encodeURIComponent(next)}`, {
          state: { challengeToken: result.challengeToken },
        });
        return;
      }
      await refresh();
      await navigate(result.status === "2fa_setup_required" ? SETUP_ROUTE : next, {
        replace: true,
      });
    } catch (caught) {
      setError(errorMessage(caught));
      setShowHint(loginErrorNeedsCredentialsHint(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      title={t("dashboard.auth.login.title")}
      subtitle={t("dashboard.auth.login.subtitle")}
      footer={
        <>
          {t("dashboard.auth.login.noAccount")}{" "}
          <Link className="font-semibold text-on-action-ghost underline" to="/signup">
            {t("dashboard.auth.login.createAccount")}
          </Link>
        </>
      }
    >
      <form onSubmit={submit} noValidate>
        <Stack gap={4}>
          {emailVerified && <Alert tone="success">{t("dashboard.auth.login.emailVerified")}</Alert>}
          {params.get("passwordChanged") === "1" && (
            <Alert tone="success">{t("dashboard.auth.login.passwordChanged")}</Alert>
          )}
          {redirectError && KNOWN_REDIRECT_ERRORS.has(redirectError) && !error && (
            <Alert tone="error">{t("auth.errors.oauthFailed")}</Alert>
          )}
          {error && (
            <Alert tone="error">
              {error} {showHint ? t("dashboard.auth.login.errorNext") : null}
            </Alert>
          )}
          <Input
            type="email"
            dir="ltr"
            autoComplete="email"
            required
            label={t("dashboard.auth.email")}
            value={email}
            {...(fieldErrors.email ? { error: fieldErrors.email } : {})}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Input
            type="password"
            autoComplete="current-password"
            required
            label={t("dashboard.auth.password")}
            value={password}
            {...(fieldErrors.password ? { error: fieldErrors.password } : {})}
            onChange={(e) => setPassword(e.target.value)}
          />
          <Checkbox
            label={t("dashboard.auth.login.rememberMe")}
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
          />
          <Button type="submit" fullWidth loading={busy}>
            {t("dashboard.auth.login.submit")}
          </Button>
          <Link className="text-sm text-on-action-ghost underline" to="/reset-password">
            {t("dashboard.auth.login.forgot")}
          </Link>
          <SocialAuthButtons />
        </Stack>
      </form>
    </AuthLayout>
  );
}
